import React, { useState, useEffect } from 'react';
import { Upload, Image, Video, Eye, Save, Power, PowerOff, ArrowLeft, Link as LinkIcon, Type, Plus, Trash2, ChevronUp, ChevronDown, ExternalLink, Home, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import AdminLayout from './AdminLayout';
import { useToast } from '../ui/toast';
// Internal pages for linking
const internalPages = [
    { label: 'Home', path: '/' },
    { label: 'About - Profile', path: '/about/profile' },
    { label: 'About - Overview', path: '/about/overview' },
    { label: 'About - History', path: '/about/history' },
    { label: 'About - Careers', path: '/about/careers' },
    { label: 'Products', path: '/products' },
    { label: 'Leasing & Loans', path: '/products/leasing-loans' },
    { label: 'News & Events', path: '/news' },
    { label: 'Contact Us', path: '/contact' },
];

const AdManagement = () => {
    const [ads, setAds] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [saveMessage, setSaveMessage] = useState('');
    const [previewMode, setPreviewMode] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({}); // Track upload progress per ad ID
    const toast = useToast();

    useEffect(() => {
        // Load existing ads from localStorage
        const storedAds = localStorage.getItem('siteAds');
        if (storedAds) {
            try {
                const adsData = JSON.parse(storedAds);
                if (Array.isArray(adsData) && adsData.length > 0) {
                    setAds(adsData);
                }
            } catch (e) {
                console.error('Error loading ads:', e);
            }
        }
    }, []);

    // Preview animation
    useEffect(() => {
        if (previewMode && ads.filter(a => a.active).length > 1) {
            const interval = setInterval(() => {
                const activeAds = ads.filter(a => a.active);
                if (activeAds.length > 1) {
                    setActiveIndex(prev => (prev + 1) % activeAds.length);
                }
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [previewMode, ads]);

    const addNewAd = () => {
        const newAd = {
            id: Date.now(),
            active: true,
            type: 'image',
            url: '',
            linkType: 'internal', // 'internal' or 'external'
            internalPath: '/about/careers',
            externalLink: '',
            title: '',
            order: ads.length
        };
        setAds([...ads, newAd]);
    };

    const updateAd = (id, field, value) => {
        setAds(ads.map(ad => ad.id === id ? { ...ad, [field]: value } : ad));
    };

    const removeAd = (id) => {
        setAds(ads.filter(ad => ad.id !== id));
    };

    const moveAd = (id, direction) => {
        const index = ads.findIndex(ad => ad.id === id);
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === ads.length - 1)) return;

        const newAds = [...ads];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newAds[index], newAds[swapIndex]] = [newAds[swapIndex], newAds[index]];
        setAds(newAds);
    };

    const handleFileUpload = async (id, e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');

        if (!isVideo && !isImage) {
            toast.error('Please upload an image or video file');
            return;
        }

        // Check file size (5MB for images, 50MB for videos)
        const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error(`File too large. Max ${isVideo ? '50MB' : '5MB'}`);
            return;
        }

        try {
            // Create unique filename
            const timestamp = Date.now();
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
            const filePath = `ads/${isVideo ? 'videos' : 'images'}/${timestamp}_${sanitizedName}`;
            const storageRef = ref(storage, filePath);

            // Start upload with progress tracking
            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(prev => ({ ...prev, [id]: progress }));
                },
                (error) => {
                    console.error('Upload error:', error);
                    toast.error('Upload failed. Please check Firebase Storage rules.');
                    setUploadProgress(prev => ({ ...prev, [id]: 0 }));
                },
                async () => {
                    // Get download URL after upload completes
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setAds(prevAds => prevAds.map(ad =>
                            ad.id === id
                                ? { ...ad, url: downloadURL, type: isVideo ? 'video' : 'image' }
                                : ad
                        ));
                        setUploadProgress(prev => ({ ...prev, [id]: 0 }));
                        toast.success('File uploaded successfully!');
                    } catch (urlError) {
                        console.error('Error getting URL:', urlError);
                        toast.error('Error getting file URL');
                        setUploadProgress(prev => ({ ...prev, [id]: 0 }));
                    }
                }
            );
        } catch (error) {
            console.error('Upload setup error:', error);
            toast.error('Error starting upload');
        }
    };

    const handleSave = () => {
        localStorage.setItem('siteAds', JSON.stringify(ads));

        // Also update the legacy activeAd for backwards compatibility
        const activeAds = ads.filter(a => a.active && a.url);
        if (activeAds.length > 0) {
            const firstActive = activeAds[0];
            localStorage.setItem('activeAd', JSON.stringify({
                active: true,
                type: firstActive.type,
                url: firstActive.url,
                link: firstActive.linkType === 'internal' ? firstActive.internalPath : firstActive.externalLink,
                title: firstActive.title,
                isInternal: firstActive.linkType === 'internal'
            }));
        } else {
            localStorage.removeItem('activeAd');
        }

        toast.success(`🎉 ${activeAds.length} ad(s) saved and now live on the website!`);
        setSaveMessage('All ads saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
    };

    const toggleAllActive = (active) => {
        setAds(ads.map(ad => ({ ...ad, active })));
    };

    const activeAds = ads.filter(a => a.active && a.url);

    return (
        <AdminLayout title="Ad Management">
            <div className="space-y-6">
                {/* Success Message */}
                {saveMessage && (
                    <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {saveMessage}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <button
                        onClick={addNewAd}
                        className="flex items-center gap-2 px-4 py-2 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#238b7f] transition-colors"
                    >
                        <Plus size={18} />
                        Add New Ad
                    </button>
                    <button
                        onClick={handleSave}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                        <Save size={18} />
                        Save All Changes
                    </button>
                    <button
                        onClick={() => toggleAllActive(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                        <Power size={18} />
                        Activate All
                    </button>
                    <button
                        onClick={() => toggleAllActive(false)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                        <PowerOff size={18} />
                        Deactivate All
                    </button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Ads List */}
                    <div className="xl:col-span-2 space-y-4">
                        {ads.length === 0 ? (
                            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                                <Image size={48} className="mx-auto text-gray-300 mb-4" />
                                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Ads Yet</h3>
                                <p className="text-gray-500 mb-4">Click "Add New Ad" to create your first advertisement</p>
                                <button
                                    onClick={addNewAd}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#238b7f] transition-colors"
                                >
                                    <Plus size={20} />
                                    Add Your First Ad
                                </button>
                            </div>
                        ) : (
                            ads.map((ad, index) => (
                                <div
                                    key={ad.id}
                                    className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all ${ad.active ? 'ring-2 ring-green-400' : 'opacity-75'}`}
                                >
                                    <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-bold text-gray-500">#{index + 1}</span>
                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${ad.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                                {ad.active ? 'ACTIVE' : 'INACTIVE'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => moveAd(ad.id, 'up')}
                                                disabled={index === 0}
                                                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                                            >
                                                <ChevronUp size={18} />
                                            </button>
                                            <button
                                                onClick={() => moveAd(ad.id, 'down')}
                                                disabled={index === ads.length - 1}
                                                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                                            >
                                                <ChevronDown size={18} />
                                            </button>
                                            <button
                                                onClick={() => updateAd(ad.id, 'active', !ad.active)}
                                                className={`p-1.5 rounded ${ad.active ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-600'}`}
                                            >
                                                {ad.active ? <Power size={16} /> : <PowerOff size={16} />}
                                            </button>
                                            <button
                                                onClick={() => removeAd(ad.id)}
                                                className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Left: Upload & Preview */}
                                            <div>
                                                {ad.url ? (
                                                    <div className="relative rounded-lg overflow-hidden bg-gray-100">
                                                        {ad.type === 'video' ? (
                                                            <video src={ad.url} className="w-full h-40 object-cover" muted />
                                                        ) : (
                                                            <img src={ad.url} alt="Ad preview" className="w-full h-40 object-cover" />
                                                        )}
                                                        <button
                                                            onClick={() => updateAd(ad.id, 'url', '')}
                                                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                ) : uploadProgress[ad.id] > 0 ? (
                                                    <div className="border-2 border-dashed border-[#2A9D8F] rounded-lg text-center h-40 flex flex-col items-center justify-center bg-[#2A9D8F]/5">
                                                        <Loader2 size={32} className="mx-auto text-[#2A9D8F] mb-2 animate-spin" />
                                                        <p className="text-sm font-semibold text-[#2A9D8F]">Uploading...</p>
                                                        <div className="w-24 bg-gray-200 rounded-full h-2 mt-2">
                                                            <div
                                                                className="bg-[#2A9D8F] h-2 rounded-full transition-all"
                                                                style={{ width: `${uploadProgress[ad.id]}%` }}
                                                            />
                                                        </div>
                                                        <p className="text-xs text-gray-500 mt-1">{Math.round(uploadProgress[ad.id])}%</p>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-[#2A9D8F] transition-colors h-40 flex flex-col items-center justify-center cursor-pointer relative"
                                                        onClick={() => document.getElementById(`ad-upload-${ad.id}`).click()}
                                                    >
                                                        <input
                                                            type="file"
                                                            accept="image/*,video/*"
                                                            onChange={(e) => handleFileUpload(ad.id, e)}
                                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                            id={`ad-upload-${ad.id}`}
                                                        />
                                                        <Upload size={32} className="mx-auto text-gray-400 mb-2 pointer-events-none" />
                                                        <p className="text-sm text-gray-600 pointer-events-none">Click to Upload Image/Video</p>
                                                        <p className="text-xs text-gray-400 mt-1 pointer-events-none">JPG, PNG, GIF, MP4, WebM (max 5MB)</p>
                                                    </div>
                                                )}

                                                {/* URL Input */}
                                                <div className="mt-3">
                                                    <input
                                                        type="url"
                                                        value={ad.url.startsWith('data:') ? '' : ad.url}
                                                        onChange={(e) => updateAd(ad.id, 'url', e.target.value)}
                                                        placeholder="Or paste image/video URL"
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                                                    />
                                                </div>
                                            </div>

                                            {/* Right: Settings */}
                                            <div className="space-y-3">
                                                {/* Title */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Ad Title (Optional)</label>
                                                    <div className="relative">
                                                        <Type size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                        <input
                                                            type="text"
                                                            value={ad.title}
                                                            onChange={(e) => updateAd(ad.id, 'title', e.target.value)}
                                                            placeholder="Title or CTA text"
                                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Link Type */}
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Link Type</label>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => updateAd(ad.id, 'linkType', 'internal')}
                                                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${ad.linkType === 'internal'
                                                                ? 'bg-[#2A9D8F] text-white'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            <Home size={14} />
                                                            Internal Page
                                                        </button>
                                                        <button
                                                            onClick={() => updateAd(ad.id, 'linkType', 'external')}
                                                            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${ad.linkType === 'external'
                                                                ? 'bg-[#2A9D8F] text-white'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            <ExternalLink size={14} />
                                                            External URL
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Internal Page Selector */}
                                                {ad.linkType === 'internal' ? (
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1">Select Page</label>
                                                        <select
                                                            value={ad.internalPath}
                                                            onChange={(e) => updateAd(ad.id, 'internalPath', e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                                                        >
                                                            {internalPages.map(page => (
                                                                <option key={page.path} value={page.path}>
                                                                    {page.label} ({page.path})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-600 mb-1">External URL</label>
                                                        <div className="relative">
                                                            <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                            <input
                                                                type="url"
                                                                value={ad.externalLink}
                                                                onChange={(e) => updateAd(ad.id, 'externalLink', e.target.value)}
                                                                placeholder="https://example.com"
                                                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Preview Panel */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Eye size={24} className="text-[#2A9D8F]" />
                                Live Preview
                            </h2>
                            <button
                                onClick={() => setPreviewMode(!previewMode)}
                                className={`px-3 py-1 rounded-full text-xs font-bold ${previewMode ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}
                            >
                                {previewMode ? '⏸ Pause' : '▶ Play'}
                            </button>
                        </div>

                        {/* Preview Container */}
                        <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden h-[400px]">
                            {/* Simulated page content */}
                            <div className="absolute inset-0 flex items-center p-6">
                                <div className="text-white/30">
                                    <p className="text-lg">Page Content Area</p>
                                    <p className="text-sm">Your website pages</p>
                                </div>
                            </div>

                            {/* Ad Preview */}
                            {activeAds.length > 0 ? (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 max-w-[200px]">
                                    {activeAds.map((ad, idx) => (
                                        <div
                                            key={ad.id}
                                            className={`relative bg-white/10 backdrop-blur-md rounded-xl overflow-hidden shadow-2xl border border-white/20 transition-all duration-500 ${idx === activeIndex % activeAds.length
                                                ? 'opacity-100 scale-100'
                                                : 'opacity-0 scale-95 absolute inset-0'
                                                }`}
                                        >
                                            {/* Ad Label */}
                                            <div className="absolute top-2 left-2 z-10 bg-[#2A9D8F] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase">
                                                Ad
                                            </div>

                                            {/* Close Button */}
                                            <div className="absolute top-2 right-2 z-10 bg-black/50 text-white rounded-full p-1">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 6L6 18M6 6l12 12" />
                                                </svg>
                                            </div>

                                            {/* Content */}
                                            {ad.type === 'video' ? (
                                                <video src={ad.url} autoPlay muted loop playsInline className="w-full h-auto max-h-[250px] object-cover" />
                                            ) : (
                                                <img src={ad.url} alt="Ad preview" className="w-full h-auto max-h-[250px] object-cover" />
                                            )}

                                            {/* Title & Link indicator */}
                                            <div className="p-2 bg-gradient-to-t from-black/80 to-transparent absolute bottom-0 left-0 right-0">
                                                {ad.title && <p className="text-white text-xs font-medium text-center">{ad.title}</p>}
                                                <p className="text-white/60 text-[10px] text-center mt-1 flex items-center justify-center gap-1">
                                                    {ad.linkType === 'internal' ? <Home size={10} /> : <ExternalLink size={10} />}
                                                    {ad.linkType === 'internal' ? ad.internalPath : 'External Link'}
                                                </p>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Dots indicator */}
                                    {activeAds.length > 1 && (
                                        <div className="flex justify-center gap-1 mt-3">
                                            {activeAds.map((_, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`w-2 h-2 rounded-full transition-all ${idx === activeIndex % activeAds.length
                                                        ? 'bg-white w-4'
                                                        : 'bg-white/40'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="text-center text-white/50">
                                        <Image size={48} className="mx-auto mb-2" />
                                        <p>No active ads to preview</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <p className="text-gray-500 text-sm mt-4 text-center">
                            Ads rotate every 3 seconds on every page
                        </p>

                        {/* Instructions */}
                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <h3 className="font-bold text-blue-800 mb-2 text-sm">How it works:</h3>
                            <ul className="text-blue-700 space-y-1 text-xs">
                                <li>• Create multiple ads that will rotate automatically</li>
                                <li>• Choose <strong>Internal Page</strong> to link to website pages (Careers, Products, etc.)</li>
                                <li>• Choose <strong>External URL</strong> for outside links</li>
                                <li>• Active ads appear on <strong>every page</strong> of the website</li>
                                <li>• Ads animate/rotate every 3 seconds</li>
                                <li>• Click "Save All Changes" to publish your ads</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdManagement;
